import clsx from 'clsx'
import { format, fromUnixTime, isValid } from 'date-fns'
import type { PropsWithChildren, ReactNode } from 'react'
import React, { useRef } from 'react'
import Checkbox from '../Checkbox/Checkbox'
import { useForwardClick } from '../hooks/useForwardClick'
import Typography from '../Typography/Typography'
import { getWidthStyle } from './ColumnHeader'
import type { ColumnWidth } from './TableContext'
import { useTable } from './TableContext'

interface CellProps {
  /**
   * Whether the cell is selected
   * @default false
   */
  selected?: boolean
  /**
   * Whether the cell should stick to the right side of the table.
   * Used for columns that contain row actions or checkboxes to select rows
   * @default false
   */
  sticky?: boolean
  /**
   * The width of the column supporting a percentage value.
   * If not provided, the column will size automatically based on its content.
   */
  width?: ColumnWidth
}

const toDate = (value: ReactNode) => {
  let dateTime = ''

  if (value instanceof Date) {
    dateTime = format(value, 'PP p')
  }

  if (typeof value === 'number') {
    dateTime = format(fromUnixTime(value), 'PP p')
  }

  return dateTime
}

function Cell({
  children,
  selected = false,
  sticky = false,
  width,
}: PropsWithChildren<CellProps>): JSX.Element {
  const ref = useRef<HTMLTableCellElement | null>(null)
  const [state] = useTable()
  const actions = useForwardClick(ref, 'input[type="checkbox"]')
  // @ts-expect-error The type property will exist on any ReactElement that is passed as a child, but will not
  // exist on any other object. Typescript however, will not obey `hasOwnProperty` checks, so we need to
  // ignore this error until we can determine a better interface
  const checkable = children && children.type === Checkbox
  const dateable =
    (typeof children === 'number' || children instanceof Date) &&
    isValid(children)

  const shouldBeSticky =
    sticky &&
    (state.columns.unselected.length > 0 ||
      Number(state.rowActions?.length) > 0)

  return (
    <td
      className={clsx(
        'px-4 text-left border-r last:border-r-0 border-black-10 focus:outline-none',
        selected ? (sticky ? 'bg-black-5-hex' : 'bg-black-5') : 'bg-white-100',
        checkable ? 'pl-3.5 pr-1.5' : state.dense ? 'py-1.5' : 'py-3',
        state.selectable ? 'first:w-[.1%] first:border-r-0' : 'first:pl-6',
        shouldBeSticky
          ? 'sticky right-0 sticky-column overflow-visible group-hover:bg-black-5-hex focus:bg-black-5-hex border-l'
          : 'group-hover:bg-black-5 focus:bg-black-5',
      )}
      onClick={checkable ? actions.handleClick : undefined}
      onKeyDown={checkable ? actions.handleKeyDown : undefined}
      ref={ref}
      role="gridcell"
      style={getWidthStyle(width)}
      tabIndex={-1}
    >
      <Typography variant="body2">
        {dateable ? toDate(children) : children}
      </Typography>
    </td>
  )
}

export default Cell
